/**
 * Redteam Constants
 * Adapted from promptfoo/redteam constants for Vortal demo flow
 */

import type { FoundationModelId, HttpMethod } from '@/types/redteam';

/**
 * Foundation model providers available for "Our AI" option
 * These match the promptfoo provider format
 */
export interface FoundationModelOption {
  value: FoundationModelId;
  label: string;
  description?: string;
}

export const FOUNDATION_MODELS: FoundationModelOption[] = [
  // OpenAI Models (Platform Managed)
  {
    value: 'openai:gpt-5.4',
    label: 'OpenAI GPT-5.4',
    description: 'OpenAI GPT-5.4 - best intelligence for agentic, coding, and professional workflows',
  },
  {
    value: 'openai:gpt-5.4-mini',
    label: 'OpenAI GPT-5.4 Mini',
    description: 'OpenAI GPT-5.4 Mini - strongest mini model for coding and subagents',
  },
  // Google Models (Platform Managed)
  {
    value: 'google:gemini-2.5-pro',
    label: 'Google Gemini 2.5 Pro',
    description: 'Google Gemini 2.5 Pro - advanced model for complex tasks',
  },
  {
    value: 'google:gemini-3.5-flash',
    label: 'Google Gemini 3.5 Flash',
    description: 'Google Gemini 3.5 Flash - optimized for speed and cost-efficiency',
  },
  {
    value: 'google:gemini-3.1-pro-preview',
    label: 'Google Gemini 3.1 Pro',
    description: 'Google Gemini 3.1 Pro - reasoning-first model for agentic workflows',
  },
  // Anthropic Models (Requires API Key)
  {
    value: 'anthropic:messages:claude-opus-4-6',
    label: 'Anthropic Claude Opus 4.6 (Requires API Key)',
    description: 'Anthropic Claude Opus 4.6 - most intelligent model for agents and coding',
  },
  {
    value: 'anthropic:messages:claude-sonnet-4-6',
    label: 'Anthropic Claude Sonnet 4.6 (Requires API Key)',
    description: 'Anthropic Claude Sonnet 4.6 - best combination of speed and intelligence',
  },
];

/**
 * Supported HTTP methods for own agent configuration
 */
export const HTTP_METHODS: HttpMethod[] = ['GET', 'POST'];

/**
 * Default configuration values
 */
export const REDTEAM_DEFAULTS = {
  MAX_CONCURRENCY: 4,
  NUM_TESTS: 3,
  DEFAULT_METHOD: 'POST' as HttpMethod,
} as const;

/**
 * Helper to check if a provider ID is a foundation model
 */
export function isFoundationModelProvider(providerId: string | undefined): boolean {
  if (!providerId) {
    return false;
  }

  const foundationPrefixes = ['openai:', 'anthropic:', 'google:', 'vertex:', 'azure:'];
  return foundationPrefixes.some((prefix) => providerId.startsWith(prefix));
}
